<template>
  <div class="matching-wizard-content">
    <!-- Steps indicator (hidden when hideSteps prop is true) -->
    <div v-if="!hideSteps" class="step-indicator mb-6">
      <div class="step" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
        <div class="step-number">1</div>
        <div class="step-label">{{ $t('matchingWizard.steps.themes') }}</div>
      </div>
      <div class="step" :class="{ active: currentStep === 2, completed: currentStep > 2 }">
        <div class="step-number">2</div>
        <div class="step-label">{{ $t('matchingWizard.steps.preferences') }}</div>
      </div>
      <div class="step" :class="{ active: currentStep === 3 }">
        <div class="step-number">3</div>
        <div class="step-label">{{ $t('matchingWizard.steps.availability') }}</div>
      </div>
    </div>

    <!-- Step 1: Topics Selection -->
    <template v-if="currentStep === 1">
      <div class="step-content">
        <p class="mb-6">{{ $t('matchingWizard.description') }}</p>

        <h3 class="mb-4">{{ $t('matchingWizard.coachingTheme') }}</h3>

        <div class="topic-selection">
          <v-chip-group v-model="selectedTopics" column multiple>
            <v-chip v-for="topic in availableTopics" :key="topic" :value="topic" filter outlined>
              {{ topic }}
            </v-chip>
          </v-chip-group>
        </div>
      </div>
    </template>

    <!-- Step 2: Availability -->
    <template v-if="currentStep === 2">
      <div class="step-content">
        <p class="mb-6">{{ $t('matchingWizard.availability.description') }}</p>

        <!-- Coaching Theme Section (from step 1) -->
        <h3 class="mb-4">{{ $t('matchingWizard.coachingTheme') }}</h3>
        <div class="topic-selection mb-6">
          <v-chip-group v-model="selectedTopics" column multiple>
            <v-chip v-for="topic in availableTopics" :key="topic" :value="topic" filter outlined>
              {{ topic }}
            </v-chip>
          </v-chip-group>
        </div>

        <!-- Preference Selection -->
        <h3 class="mb-4">{{ $t('matchingWizard.preferences.speakWithQuestion') }}</h3>
        <div class="preference-selection mb-6">
          <v-chip-group v-model="selectedPreference" class="d-flex flex-wrap gap-2">
            <v-chip value="mentor" filter outlined class="ma-1">
              {{ $t('matchingWizard.preferences.mentor') }}
            </v-chip>
            <v-chip value="coach" filter outlined class="ma-1">
              {{ $t('matchingWizard.preferences.coach') }}
            </v-chip>
          </v-chip-group>
        </div>

        <!-- Conditional Pricing Notice (only shown when Coach is selected) -->
        <div v-if="selectedPreference === 'coach'" class="pricing-notice">
          <p>{{ $t('matchingWizard.availability.pricingNotice') }}</p>
        </div>
      </div>
    </template>

    <!-- Step 3: Timing Preference -->
    <template v-if="currentStep === 3">
      <div class="step-content">
        <p class="mb-6">{{ $t('matchingWizard.timing.description') }}</p>

        <!-- Coaching Theme Section (from step 1) -->
        <h3 class="mb-4">{{ $t('matchingWizard.coachingTheme') }}</h3>
        <div class="topic-selection mb-6">
          <v-chip-group v-model="selectedTopics" column multiple>
            <v-chip v-for="topic in availableTopics" :key="topic" :value="topic" filter outlined>
              {{ topic }}
            </v-chip>
          </v-chip-group>
        </div>

        <!-- Preference Selection (from step 2) -->
        <h3 class="mb-4">{{ $t('matchingWizard.preferences.speakWithQuestion') }}</h3>
        <div class="preference-selection mb-6">
          <v-chip-group v-model="selectedPreference" class="d-flex flex-wrap gap-2">
            <v-chip value="mentor" filter outlined class="ma-1">
              {{ $t('matchingWizard.preferences.mentor') }}
            </v-chip>
            <v-chip value="coach" filter outlined class="ma-1">
              {{ $t('matchingWizard.preferences.coach') }}
            </v-chip>
          </v-chip-group>
        </div>

        <!-- Timing Preference Selection (new for step 3) -->
        <h3 class="mb-4">{{ $t('matchingWizard.timing.question') }}</h3>
        <div class="timing-selection">
          <v-chip-group v-model="selectedTiming" class="d-flex flex-wrap gap-2">
            <v-chip value="morning" filter outlined class="ma-1">
              {{ $t('matchingWizard.timing.morning') }}
            </v-chip>
            <v-chip value="afternoon" filter outlined class="ma-1">
              {{ $t('matchingWizard.timing.afternoon') }}
            </v-chip>
            <v-chip value="noPreference" filter outlined class="ma-1">
              {{ $t('matchingWizard.timing.noPreference') }}
            </v-chip>
          </v-chip-group>
        </div>
      </div>
    </template>

    <!-- Navigation buttons -->
    <div class="d-flex justify-space-between mt-6">
      <v-btn
        outlined
        color="grey"
        @click="previousStep"
        :disabled="currentStep === 1"
        x-large
        min-width="240"
        height="56"
        class="text-none"
      >
        {{ $t('matchingWizard.back') }}
      </v-btn>

      <v-btn
        :color="!canProceed ? 'grey lighten-2' : 'black'"
        :dark="canProceed"
        @click="nextStep"
        :disabled="!canProceed"
        x-large
        min-width="240"
        height="56"
        class="text-none"
        :class="{ 'disabled-button': !canProceed }"
      >
        {{ currentStep === 3 ? $t('matchingWizard.finish') : $t('matchingWizard.next') }}
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

export default Vue.extend({
  name: 'MMatchingWizardContent',
  props: {
    hideSteps: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentStep: 1,
      selectedTopics: [],
      selectedPreference: null,
      selectedTiming: null,
      availableTopics: ['Work-Life-Balance', 'Career Development', 'Führungsthemen'],
    };
  },
  computed: {
    canProceed() {
      switch (this.currentStep) {
        case 1:
          return this.selectedTopics.length > 0;
        case 2:
          return this.selectedPreference !== null;
        case 3:
          return this.selectedTiming !== null;
        default:
          return false;
      }
    },
  },
  watch: {
    currentStep(newStep) {
      this.$emit('step-changed', newStep);
    },
  },
  methods: {
    nextStep() {
      if (this.currentStep < 3) {
        this.currentStep++;
      } else {
        // Finish wizard
        this.finishWizard();
      }
    },
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    finishWizard() {
      // Emit wizard completion event with collected data
      this.$emit('wizard-completed', {
        topics: this.selectedTopics,
        preference: this.selectedPreference,
        timing: this.selectedTiming,
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.matching-wizard-content {
  padding: 0;
}

.step-indicator {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    position: relative;

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      top: 16px;
      right: -50%;
      width: 100%;
      height: 2px;
      background-color: #e0e0e0;
      z-index: 0;
    }

    &.completed::after {
      background-color: var(--v-primary-base);
    }

    .step-number {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background-color: #e0e0e0;
      color: #666;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      margin-bottom: 0.5rem;
      z-index: 1;
      position: relative;
    }

    &.active .step-number {
      background-color: var(--v-primary-base);
      color: white;
    }

    &.completed .step-number {
      background-color: var(--v-primary-base);
      color: white;
    }

    .step-label {
      font-size: 0.875rem;
      text-align: center;
      color: #666;
      z-index: 1;
    }

    &.active .step-label {
      color: var(--v-primary-base);
      font-weight: 500;
    }
  }
}

.step-content {
  min-height: 300px;
}

.topic-selection {
  max-height: 300px;
  overflow-y: auto;
}

.disabled-button {
  opacity: 1 !important;
  color: #9e9e9e !important;
  background-color: #f5f5f5 !important;
  cursor: not-allowed !important;

  &:hover {
    background-color: #f5f5f5 !important;
  }
}
</style>
